import * as React from 'react';
import { Box } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { UserMessageConfirmationModal } from '../modals/UserMessageModals';
import { GridRowModesModel, GridRowModes, GridActionsCellItem, GridEventListener, GridRowId } from '@mui/x-data-grid';
import { DataGrid, GridColDef, GridRowEditStopReasons, GridRowModel } from '@mui/x-data-grid';

export const EditableTable = ({ columns, rows, setAllRows, processRowUpdate, processRowDelete }:
    {
        columns: GridColDef[],
        rows: any[],
        setAllRows: (value: React.SetStateAction<any[]>) => void,
        processRowUpdate?: (newRow: GridRowModel) => GridRowModel,
        processRowDelete?: (rowId: number) => void
    }) => {
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const [confirmOpenState, setConfirmOpenState] = React.useState<boolean>(false);
    const [idForDelete, setIdForDelete] = React.useState<number>(0);

    const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const handleEditClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleCancelClick = (id: GridRowId) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });
    };

    const handleSaveClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleDeleteClick = (id: GridRowId) => () => {
        // create a conformation dialog before deleting !!
        if (processRowDelete) {
            setIdForDelete(id as number);
            setConfirmOpenState(true);
        }
    };

    const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const columnsExtended: GridColDef[] = processRowUpdate ? [
        ...columns,
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon />}
                            label="Save"
                            onClick={handleSaveClick(id)}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon />}
                            label="Cancel"
                            className="textPrimary"
                            onClick={handleCancelClick(id)}
                            color="inherit"
                        />,
                    ];
                }

                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />,
                    processRowDelete ? <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        className="textPrimary"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    /> : <></>,
                ];
            },
        },
    ] : [ ...columns, ];

    return <React.Fragment >
        {processRowDelete && <UserMessageConfirmationModal
            userMessage={"Are you sure you want to delete this item?"} inputId={idForDelete} processConfirmation={processRowDelete} openState={confirmOpenState} setOpenState={setConfirmOpenState} />}
        <Box sx={{ flexGrow: 1, width: "100%" }} style={{ overflow: 'auto' }}>
            <DataGrid
                columns={columnsExtended}
                rows={rows}
                editMode="row"
                rowModesModel={rowModesModel}
                columnVisibilityModel={{ id: true }}
                sx={{
                    "& .selectRowClass": {
                        color: 'primary.main'
                    },
                }}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                slotProps={{
                    toolbar: { setAllRows, setRowModesModel },
                }}
            />
        </Box>
    </React.Fragment >
}